import React from 'react';

// Large trend chart for expanded view (with dates and price labels).
// When every entry has price_per_lb (typical for food), plot $/lb so catch-weight items
// (where case_price varies with pack weight) don't look like they're changing price when they aren't.
export default function TrendChart({ data, width = 500, height = 160 }) {
  if (!data || data.length < 1) return <span className="text-xs text-gray-400">No history yet</span>;
  const usePerLb = data.every(d => d.price_per_lb != null && isFinite(Number(d.price_per_lb)));
  const values = usePerLb ? data.map(d => Number(d.price_per_lb)) : data.map(d => Number(d.price));
  const labelSuffix = usePerLb ? '/lb' : '';
  const min = Math.min(...values);
  const max = Math.max(...values);
  // Treat tiny variance as flat: <1% relative spread OR <$0.01 absolute spread.
  // Prevents float-drift rounding errors from drawing a fake upward/downward trend when the
  // real price never changed (e.g. catch-weight items with $4.71/lb on every invoice).
  const median = values.slice().sort((a, b) => a - b)[Math.floor(values.length / 2)];
  const absSpread = max - min;
  const relSpread = median > 0 ? absSpread / median : 0;
  const flat = absSpread < 0.01 || relSpread < 0.01;
  const range = flat ? 1 : absSpread;
  const padY = 24;
  const padX = 30;
  const chartW = width - padX * 2;
  const chartH = height - padY * 2;
  const pts = values.map((v, i) => ({
    x: padX + (data.length === 1 ? chartW / 2 : (i / (data.length - 1)) * chartW),
    // When flat, render every point at the vertical center so the line is a clean horizontal.
    y: flat ? padY + chartH / 2 : padY + chartH - ((v - min) / range) * chartH,
    value: v,
    date: data[i].date,
  }));
  const polyline = pts.map(p => `${p.x},${p.y}`).join(' ');
  const trending = values[values.length - 1] > values[0];
  const color = flat ? '#9CA3AF' : (trending ? '#DC2626' : '#2D8F5E');
  // Fill area
  const area = `${pts[0].x},${padY + chartH} ${polyline} ${pts[pts.length - 1].x},${padY + chartH}`;
  return (
    <svg width={width} height={height} className="w-full" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
      <polygon fill={flat ? 'rgba(156,163,175,0.06)' : (trending ? 'rgba(220,38,38,0.06)' : 'rgba(45,143,94,0.06)')} points={area} />
      <polyline fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" points={polyline} />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="4" fill="white" stroke={color} strokeWidth="2" />
          <text x={p.x} y={p.y - 10} textAnchor="middle" fontSize="11" fontWeight="600" fill="#111827">
            ${p.value.toFixed(2)}{labelSuffix}
          </text>
          <text x={p.x} y={padY + chartH + 16} textAnchor="middle" fontSize="10" fill="#9CA3AF">
            {new Date(p.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </text>
        </g>
      ))}
    </svg>
  );
}
